import axios, {AxiosBasicCredentials, AxiosInstance} from 'axios';
import {clearToken, retrieveToken} from '../services/tokenVerify';
import store from '../store';
import {loggingIn} from '../store/ducks/login/actions';

const api = axios.create({
    baseURL: process.env.REACT_APP_HOST,
});

/** Texto que a API devolve quando o token de sessão não vale mais. */
const ERRO_TOKEN = 'precisa de um token válido';

/** Código que a API manda no 403 de "logado, mas sem permissão" (#484909). */
const ACESSO_NEGADO = 'ACESSO_NEGADO';

let ultimaAtualizacaoAcesso = 0;

/**
 * Um 403 de acesso negado pode ser perfil tirado com a tela aberta. Recarrega o
 * acesso da sessão para o menu e a guarda da página refletirem isso, sem
 * deslogar. No máximo uma vez a cada 10 s: várias recusas seguidas na mesma
 * tela não viram uma rajada de chamadas.
 */
const atualizarAcesso = () => {
    const agora = Date.now();
    const token = retrieveToken();
    if (!token || agora - ultimaAtualizacaoAcesso < 10000) {
        return;
    }
    ultimaAtualizacaoAcesso = agora;
    api.get(`/auth/retrieve/${token}`)
        .then(resposta => store.dispatch(loggingIn(resposta.data)))
        .catch(() => {
            // Sessão inválida: a próxima chamada da tela leva ao login.
        });
};

/**
 * Sessão expirada manda para o login, em vez de virar erro da tela.
 *
 * Sem isso o token vencido chega em cada tela como um erro qualquer, e cada uma
 * mostra a mensagem que tem à mão: "Não consegui carregar o histórico", grid
 * vazio, spinner que não termina. Nenhuma delas diz o que realmente aconteceu,
 * e a única saída era o usuário desconfiar e recarregar a página.
 */
const tratarSessaoExpirada = (instancia: AxiosInstance): AxiosInstance => {
    instancia.interceptors.response.use(
        resposta => resposta,
        motivo => {
            const status = motivo?.response?.status;
            const erro = String(motivo?.response?.data?.error || '');

            // Só a mensagem de token: 400 é o status que a API usa para
            // qualquer recusa, então o texto é o que separa sessão vencida de
            // erro de validação comum.
            if ((status === 400 || status === 401 || status === 403) && erro.indexOf(ERRO_TOKEN) !== -1) {
                clearToken();
                if (window.location.hash.indexOf('/login') === -1) {
                    window.location.hash = '#/login';
                }
            } else if (status === 403 && motivo?.response?.data?.codigo === ACESSO_NEGADO) {
                // Sem permissão não é sessão vencida: a tela mostra o erro e o
                // usuário continua logado.
                atualizarAcesso();
            }

            return Promise.reject(motivo);
        }
    );

    return instancia;
};

export const getApi = function (){
    const token = retrieveToken();
    let url = process.env.REACT_APP_HOST;
    const api = axios.create({
        baseURL: url,
        params: {_system_token: token}
    });

    // Tela que manda os proprios params como array (ex.: dashboard do
    // financeiro) substitui o params padrao acima, e o token sumia da
    // chamada. Passava porque essas rotas nao exigiam login; com os perfis
    // (#484909) exigem, entao o token entra aqui em qualquer formato.
    api.interceptors.request.use(config => {
        const params = config.params;
        config.params = Array.isArray(params)
            ? {...params, _system_token: token}
            : {...(params || {}), _system_token: token};
        return config;
    });

    return tratarSessaoExpirada(api);
}

export default api;
